import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { aiApi } from '../utils/apiUtils';
import MarkdownRenderer from './MarkdownRenderer';

// Incremental renderer for the AI summary SSE stream.
//
// Renders the accumulated markdown buffer through MarkdownRenderer on every
// chunk — partial headings / unclosed emphasis look momentarily off but
// resolve as soon as the next chunk lands, and this keeps math + sectioning
// consistent between the streaming and final views. The `done` event is
// still useful for surfacing extracted links and notifying the parent.
//
// Props:
//   - paperId   (required) — arXiv id; used in the /ai/summary POST body
//   - compact   (optional) — render in the smaller PaperCard style
//   - onDone    (optional) — fires once with the {summary_html, website_link, code_link}
//                            payload so the parent can refresh eligibility, etc.
//   - onError   (optional) — fires on any SSE error with the Error object
const StreamingSummary = ({ paperId, compact = false, onDone, onError }) => {
  const [buffer, setBuffer] = useState('');
  const [streaming, setStreaming] = useState(true);
  const [meta, setMeta] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [websiteLink, setWebsiteLink] = useState('');
  const [codeLink, setCodeLink] = useState('');

  // Latest-ref pattern so the effect doesn't re-run every time the parent
  // re-creates onDone/onError callbacks.
  const onDoneRef = useRef(onDone);
  const onErrorRef = useRef(onError);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (!paperId) return undefined;

    setBuffer('');
    setStreaming(true);
    setMeta(null);
    setErrorMsg('');
    setWebsiteLink('');
    setCodeLink('');

    const controller = aiApi.streamSummary(paperId, {
      onMeta: (data) => {
        setMeta(data || {});
      },
      onChunk: (data) => {
        if (!data?.text) return;
        setBuffer((prev) => prev + data.text);
      },
      onDone: (data) => {
        setStreaming(false);
        // Prefer the full markdown from the done payload — the stream may
        // have emitted truncated or out-of-order chunks, and the server
        // sends the canonical text here (both fresh + cached flows).
        if (data?.summary) setBuffer(data.summary);
        if (data?.website_link) setWebsiteLink(data.website_link);
        if (data?.code_link) setCodeLink(data.code_link);
        if (onDoneRef.current) onDoneRef.current(data || {});
      },
      onError: (err) => {
        setStreaming(false);
        setErrorMsg(err?.message || 'Failed to generate summary');
        if (onErrorRef.current) onErrorRef.current(err);
      }
    });

    return () => {
      // Sync AbortController returned straight from postSse — we can call
      // abort() immediately. postSse drops events from the aborted stream
      // so React 18 strict mode double-mounting is a no-op.
      if (controller) controller.abort();
    };
  }, [paperId]);

  if (errorMsg) {
    return (
      <div className="text-xs text-red-500 italic">{errorMsg}</div>
    );
  }

  const containerClass = compact
    ? 'text-xs text-gray-700'
    : 'text-sm text-gray-700';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {meta?.cached && streaming && (
        <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-2">
          Replaying cached summary
        </div>
      )}
      <div className="relative">
        <MarkdownRenderer
          content={buffer}
          compact={compact}
          className={containerClass}
        />
        {streaming && (
          <span className="inline-block w-1.5 h-3 ml-0.5 bg-purple-500 align-middle animate-pulse" />
        )}
      </div>
      {!streaming && (websiteLink || codeLink) && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          {websiteLink && (
            <a
              href={websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 underline hover:text-purple-800"
            >
              Project site
            </a>
          )}
          {codeLink && (
            <a
              href={codeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 underline hover:text-purple-800"
            >
              Code
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default StreamingSummary;
